import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/apiClient';
import { useAuth } from '@/context/AuthContext';

export interface ScopedMemberGroup {
  id: string;
  name: string;
  role: string;
  joinedAt: string;
}

/** GET /roster/scoped/:userId — basic profile + group memberships. */
export interface ScopedMemberDetail {
  id: string;
  displayName: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  profileImageUrl: string | null;
  dateOfBirth: string | null;
  gender: string | null;
  groups: ScopedMemberGroup[];
}

interface ScopedMemberDetailApiResponse {
  success: boolean;
  data: ScopedMemberDetail;
}

/**
 * Member detail for the org-level roster (leadership roles only). No
 * group-specific data — attendance/health live in the group-context detail.
 * Gated on org alignment like useScopedRoster (scope comes from org context).
 */
export function useScopedMemberDetail(orgId: string | undefined, userId: string | undefined) {
  const { currentOrganization, isSwitchingOrg } = useAuth();

  return useQuery({
    queryKey: ['scoped-member-detail', orgId, userId],
    queryFn: async (): Promise<ScopedMemberDetail> => {
      const response = await api.get<ScopedMemberDetailApiResponse>(`/roster/scoped/${userId}`);
      return response.data;
    },
    enabled: !!orgId && !!userId && !isSwitchingOrg && currentOrganization?.id === orgId,
    staleTime: 2 * 60 * 1000,
  });
}
